import React, { useEffect } from "react";
import SVG from "react-inlinesvg";
import { Modal } from "react-bootstrap";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import cogoToast from "cogo-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Form as FormLayout } from "react-bootstrap";
import TextInput from "../../../Common/components/text-input";
import { useEditSecurityQuestionMutation, useGetUserGroupsQuery } from "../../../../../redux/apiSlice";

const normalizeUserGroups = (value) => {
    const list = Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [];
    return list.map((group) => ({
        value: group.groupId ?? group.id ?? group.key ?? group.name,
        label: group.name ?? group.groupId ?? group.id ?? group.key,
    })).filter((group) => group.value);
};

const EditSecurityQuestionModal = ({ showEditSecurityQuestionModal, onHide, details, tableRef }) => {
    const [editSecurityQuestion, { isLoading }] = useEditSecurityQuestionMutation();
    const { data: userGroupsData } = useGetUserGroupsQuery();
    const userGroups = normalizeUserGroups(userGroupsData);

    const validationSchema = Yup.object({
        question: Yup.string()
            .required('Security question is required')
            .min(10, 'Question must be at least 10 characters')
            .max(200, 'Question must not exceed 200 characters'),
        userGroup: Yup.string().required('User group is required'),
    });

    // Get initial values from the details prop
    const initialValues = {
        question: details?.question || "",
        userGroup: details?.userGroup || "trove",
        securityQuestionId: details?.id || details?.securityQuestionId || ""
    };

    return (
        <Modal
            show={showEditSecurityQuestionModal}
            onHide={onHide}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={isLoading ? "static" : true}
            keyboard={!isLoading}
        >
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    const payload = {
                        userGroup: values.userGroup,
                        question: values.question.trim(),
                        securityQuestionId: values.securityQuestionId
                    };

                    editSecurityQuestion(payload)
                        .unwrap()
                        .then(() => {
                            cogoToast.success("Security question updated successfully");
                            // Refresh the table
                            if (tableRef?.current?.refresh) {
                                tableRef.current.refresh();
                            }
                            onHide();
                        })
                        .catch((error) => {
                            cogoToast.error(
                                error?.data?.message || "Failed to update security question"
                            );
                        });
                }}
            >
                {({ isValid, values, handleChange, dirty }) => (
                    <Form noValidate>
                        <Modal.Header>
                            <Modal.Title id="contained-modal-title-vcenter">
                                <span className="svg-icon svg-icon-lg svg-icon-primary mr-3">
                                    <SVG
                                        title=""
                                        src={toAbsoluteUrl("/media/svg/icons/Shopping/Safe.svg")}
                                    />
                                </span>
                                Edit Security Question
                            </Modal.Title>
                            <button type="button" className="close" onClick={onHide}>
                                <span aria-hidden="true" className="d-block">×</span>
                                <span className="sr-only">Close</span>
                            </button>
                        </Modal.Header>
                        
                        <Modal.Body className="px-10 py-5">
                            <FormLayout.Row>
                                <div style={{ width: "100%" }}>
                                    <FormLayout.Group>
                                        <FormLayout.Label>User Group</FormLayout.Label>
                                        <FormLayout.Control
                                            as="select"
                                            name="userGroup"
                                            value={values.userGroup}
                                            onChange={handleChange}
                                        >
                                            {userGroups.length === 0 && (
                                                <option value={values.userGroup}>{values.userGroup}</option>
                                            )}
                                            {userGroups.map((group) => (
                                                <option key={group.value} value={group.value}>
                                                    {group.label}
                                                </option>
                                            ))}
                                        </FormLayout.Control>
                                        <small className="form-text text-muted">
                                            Moving this to a different group changes who is asked this question.
                                        </small>
                                    </FormLayout.Group>
                                </div>
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <div style={{ width: "100%" }}>
                                    <TextInput
                                        label="Security Question"
                                        name="question"
                                        value={values.question}
                                        placeholder="e.g., What was the name of your first pet?"
                                        onChange={handleChange}
                                        as="textarea"
                                        rows={3}
                                    />
                                    <small className="form-text text-muted">
                                        Update the security question that users will answer.
                                    </small>
                                </div>
                            </FormLayout.Row>

                            {/* Hidden field for securityQuestionId */}
                            <input 
                                type="hidden" 
                                name="securityQuestionId" 
                                value={values.securityQuestionId} 
                            />
                        </Modal.Body>

                        <Modal.Footer>
                            <button
                                type="button"
                                onClick={onHide}
                                className="btn btn-light btn-elevate px-4"
                                disabled={isLoading}
                            >
                                Close
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary btn-elevate px-4 ml-4"
                                disabled={isLoading || !isValid || !dirty}
                            >
                                {isLoading ? "Updating..." : "Update"}
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default EditSecurityQuestionModal;